import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../services/apiClient';
import './CourseComparison.css';

const CourseComparison = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [comparisonData, setComparisonData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCriteria, setSelectedCriteria] = useState([
    'financial', 'academic', 'practical', 'career'
  ]);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [comparisonName, setComparisonName] = useState('');

  useEffect(() => {
    const courseIds = location.state?.courseIds;
    if (courseIds && courseIds.length >= 2) {
      loadComparisonData(courseIds);
    } else {
      navigate('/career/recommendations');
    }
  }, [location.state, navigate]);

  const loadComparisonData = async (courseIds) => {
    try {
      const { data } = await api.post('/career/compare-courses/', {
        course_ids: courseIds
      });
      setComparisonData(data);
    } catch (error) {
      console.error('Error loading comparison data:', error);
      navigate('/career/recommendations');
    } finally {
      setLoading(false);
    }
  };

  const saveComparison = async () => {
    if (!comparisonName.trim()) return;

    try {
      await api.post('/career/comparisons/', {
        name: comparisonName,
        courses: comparisonData.courses.map(course => course.id),
        comparison_criteria: selectedCriteria,
        notes: `Comparison created on ${new Date().toLocaleDateString()}`
      });
      setSaveDialogOpen(false);
      setComparisonName('');
    } catch (error) {
      console.error('Error saving comparison:', error);
    }
  };

  const toggleCriteria = (criteria) => {
    setSelectedCriteria(prev => 
      prev.includes(criteria)
        ? prev.filter(c => c !== criteria)
        : [...prev, criteria]
    );
  };

  if (loading) {
    return (
      <div className="comparison-loading">
        <div className="loading-spinner"></div>
        <p>Preparing course comparison...</p>
      </div>
    );
  }

  if (!comparisonData) {
    return (
      <div className="comparison-error">
        <h2>Unable to load comparison data</h2>
        <button onClick={() => navigate('/career/recommendations')}>
          Back to Recommendations
        </button>
      </div>
    );
  }

  return (
    <div className="course-comparison">
      <div className="comparison-header">
        <h1>📊 Course Comparison</h1>
        <p>Compare training programs side-by-side to make an informed decision.</p>
        
        <div className="header-actions">
          <button 
            className="btn-secondary"
            onClick={() => navigate('/career/recommendations')}
          >
            ← Back to Recommendations
          </button>
          
          <button 
            className="btn-primary"
            onClick={() => setSaveDialogOpen(true)}
          >
            💾 Save Comparison
          </button>
        </div>
      </div>

      <div className="criteria-selector">
        <h3>Select Comparison Criteria:</h3>
        <div className="criteria-buttons">
          {[
            { key: 'financial', label: '💰 Financial', icon: '💰' },
            { key: 'academic', label: '🎓 Academic', icon: '🎓' },
            { key: 'practical', label: '⚙️ Practical', icon: '⚙️' },
            { key: 'career', label: '🚀 Career Outcomes', icon: '🚀' }
          ].map(criteria => (
            <button
              key={criteria.key}
              className={`criteria-btn ${selectedCriteria.includes(criteria.key) ? 'active' : ''}`}
              onClick={() => toggleCriteria(criteria.key)}
            >
              {criteria.icon} {criteria.label}
            </button>
          ))}
        </div>
      </div>

      <div className="comparison-table">
        <div className="table-header">
          <div className="criteria-column">Criteria</div>
          {comparisonData.courses.map(course => (
            <div key={course.id} className="course-column">
              <h3>{course.programme_name}</h3>
              <div className="course-type">{course.course_type?.replace('_', ' ')}</div>
            </div>
          ))}
        </div>

        <div className="table-body">
          {selectedCriteria.includes('financial') && (
            <FinancialComparison courses={comparisonData.courses} />
          )}
          
          {selectedCriteria.includes('academic') && (
            <AcademicComparison courses={comparisonData.courses} />
          )}
          
          {selectedCriteria.includes('practical') && (
            <PracticalComparison courses={comparisonData.courses} />
          )}
          
          {selectedCriteria.includes('career') && (
            <CareerComparison courses={comparisonData.courses} />
          )}
        </div>
      </div>

      {comparisonData.comparison_summary && (
        <ComparisonSummary summary={comparisonData.comparison_summary} />
      )}

      {saveDialogOpen && (
        <SaveComparisonDialog
          comparisonName={comparisonName}
          setComparisonName={setComparisonName}
          onSave={saveComparison}
          onCancel={() => setSaveDialogOpen(false)}
        />
      )}
    </div>
  );
};

const FinancialComparison = ({ courses }) => (
  <div className="comparison-section">
    <div className="section-header">💰 Financial Information</div>
    
    <div className="comparison-row">
      <div className="criteria-label">Monthly Stipend</div>
      {courses.map(course => (
        <div key={course.id} className="comparison-cell">
          {course.stipend_amount > 0 
            ? `R${course.stipend_amount.toLocaleString()}`
            : 'No stipend'
          }
        </div>
      ))}
    </div>

    <div className="comparison-row">
      <div className="criteria-label">Potential Starting Salary</div>
      {courses.map(course => (
        <div key={course.id} className="comparison-cell">
          {course.career_outcome?.average_starting_salary
            ? `R${course.career_outcome.average_starting_salary.toLocaleString()}`
            : 'Not specified'
          }
        </div>
      ))}
    </div>

    <div className="comparison-row">
      <div className="criteria-label">Salary Range</div>
      {courses.map(course => (
        <div key={course.id} className="comparison-cell">
          {course.career_outcome?.salary_range_min && course.career_outcome?.salary_range_max
            ? `R${course.career_outcome.salary_range_min.toLocaleString()} - R${course.career_outcome.salary_range_max.toLocaleString()}`
            : 'Not specified'
          }
        </div>
      ))}
    </div>

    <div className="comparison-row">
      <div className="criteria-label">ROI Potential</div>
      {courses.map(course => (
        <div key={course.id} className="comparison-cell">
          {course.comparison_metrics?.financial?.potential_roi?.potential_annual_salary
            ? `R${course.comparison_metrics.financial.potential_roi.potential_annual_salary.toLocaleString()}/year`
            : 'Calculate based on career outcomes'
          }
        </div>
      ))}
    </div>
  </div>
);

const AcademicComparison = ({ courses }) => (
  <div className="comparison-section">
    <div className="section-header">🎓 Academic Information</div>
    
    <div className="comparison-row">
      <div className="criteria-label">NQF Level</div>
      {courses.map(course => (
        <div key={course.id} className="comparison-cell">
          {course.nqf_exit_level || 'Not specified'}
        </div>
      ))}
    </div>

    <div className="comparison-row">
      <div className="criteria-label">Qualification Credits</div>
      {courses.map(course => (
        <div key={course.id} className="comparison-cell">
          {course.qualification_credits || 'Not specified'}
        </div>
      ))}
    </div>

    <div className="comparison-row">
      <div className="criteria-label">SAQA Registration</div>
      {courses.map(course => (
        <div key={course.id} className="comparison-cell">
          {course.saqa_id ? (
            <span className="status-badge positive">✓ Registered</span>
          ) : (
            <span className="status-badge neutral">Not registered</span>
          )}
        </div>
      ))}
    </div>

    <div className="comparison-row">
      <div className="criteria-label">Accreditation Status</div>
      {courses.map(course => (
        <div key={course.id} className="comparison-cell">
          <span className={`status-badge ${course.accreditation_status === 'approved' ? 'positive' : 'neutral'}`}>
            {course.accreditation_status?.replace('_', ' ')}
          </span>
        </div>
      ))}
    </div>
  </div>
);

const PracticalComparison = ({ courses }) => (
  <div className="comparison-section">
    <div className="section-header">⚙️ Practical Information</div>
    
    <div className="comparison-row">
      <div className="criteria-label">Duration</div>
      {courses.map(course => (
        <div key={course.id} className="comparison-cell">
          {course.duration_months ? `${course.duration_months} months` : 'Not specified'}
        </div>
      ))}
    </div>

    <div className="comparison-row">
      <div className="criteria-label">Study Format</div>
      {courses.map(course => (
        <div key={course.id} className="comparison-cell">
          {course.qualification_type?.replace('_', ' ') || 'Not specified'}
        </div>
      ))}
    </div>

    <div className="comparison-row">
      <div className="criteria-label">Campus Location</div>
      {courses.map(course => (
        <div key={course.id} className="comparison-cell">
          {course.comparison_metrics?.practical?.campus_location || 'Not specified'}
        </div>
      ))}
    </div>

    <div className="comparison-row">
      <div className="criteria-label">Virtual Option</div>
      {courses.map(course => (
        <div key={course.id} className="comparison-cell">
          {course.comparison_metrics?.practical?.is_virtual ? (
            <span className="status-badge positive">✓ Available</span>
          ) : (
            <span className="status-badge neutral">In-person only</span>
          )}
        </div>
      ))}
    </div>
  </div>
);

const CareerComparison = ({ courses }) => (
  <div className="comparison-section">
    <div className="section-header">🚀 Career Outcomes</div>
    
    <div className="comparison-row">
      <div className="criteria-label">Employment Rate</div>
      {courses.map(course => (
        <div key={course.id} className="comparison-cell">
          {course.career_outcome?.employment_rate
            ? `${course.career_outcome.employment_rate}%`
            : 'Not available'
          }
        </div>
      ))}
    </div>

    <div className="comparison-row">
      <div className="criteria-label">Industry Demand</div>
      {courses.map(course => (
        <div key={course.id} className="comparison-cell">
          {course.career_outcome?.industry_demand_level ? (
            <span className={`demand-badge ${course.career_outcome.industry_demand_level.replace('_', '-')}`}>
              {course.career_outcome.industry_demand_level.replace('_', ' ')}
            </span>
          ) : (
            'Not specified'
          )}
        </div>
      ))}
    </div>

    <div className="comparison-row">
      <div className="criteria-label">Target Job Roles</div>
      {courses.map(course => (
        <div key={course.id} className="comparison-cell">
          {course.career_outcome?.target_job_roles?.length > 0 ? (
            <ul className="job-roles-list">
              {course.career_outcome.target_job_roles.slice(0, 3).map((role, index) => (
                <li key={index}>{role.title}</li>
              ))}
              {course.career_outcome.target_job_roles.length > 3 && (
                <li>+{course.career_outcome.target_job_roles.length - 3} more</li>
              )}
            </ul>
          ) : (
            'Not specified'
          )}
        </div>
      ))}
    </div>

    <div className="comparison-row">
      <div className="criteria-label">Skills Taught</div>
      {courses.map(course => (
        <div key={course.id} className="comparison-cell">
          {course.career_outcome?.skills_taught?.length > 0 ? (
            <div className="skills-tags">
              {course.career_outcome.skills_taught.slice(0, 4).map((skill, index) => (
                <span key={index} className="skill-tag">{skill.name}</span>
              ))}
              {course.career_outcome.skills_taught.length > 4 && (
                <span className="skill-tag more">+{course.career_outcome.skills_taught.length - 4}</span>
              )}
            </div>
          ) : (
            'Not specified'
          )}
        </div>
      ))}
    </div>
  </div>
);

const ComparisonSummary = ({ summary }) => (
  <div className="comparison-summary">
    <h3>📈 Comparison Highlights</h3>
    <div className="summary-grid">
      {summary.highest_employment_rate && (
        <div className="summary-item">
          <div className="summary-icon">📊</div>
          <div className="summary-content">
            <strong>Highest Employment Rate</strong>
            <p>{summary.highest_employment_rate.course_name} ({summary.highest_employment_rate.rate}%)</p>
          </div>
        </div>
      )}
      
      {summary.highest_salary_potential && (
        <div className="summary-item">
          <div className="summary-icon">💰</div>
          <div className="summary-content">
            <strong>Highest Salary Potential</strong>
            <p>{summary.highest_salary_potential.course_name} (R{summary.highest_salary_potential.salary.toLocaleString()})</p>
          </div>
        </div>
      )}
      
      {summary.shortest_duration && (
        <div className="summary-item">
          <div className="summary-icon">⏱️</div>
          <div className="summary-content">
            <strong>Shortest Duration</strong>
            <p>{summary.shortest_duration.course_name} ({summary.shortest_duration.months} months)</p>
          </div>
        </div>
      )}
      
      {summary.virtual_options?.length > 0 && (
        <div className="summary-item">
          <div className="summary-icon">💻</div>
          <div className="summary-content">
            <strong>Virtual Learning Available</strong>
            <p>{summary.virtual_options.join(', ')}</p>
          </div>
        </div>
      )}
    </div>
  </div>
);

const SaveComparisonDialog = ({ comparisonName, setComparisonName, onSave, onCancel }) => (
  <div className="dialog-overlay">
    <div className="dialog">
      <h3>💾 Save Comparison</h3>
      <p>Give your comparison a name to save it for later reference.</p>
      
      <input
        type="text"
        value={comparisonName}
        onChange={(e) => setComparisonName(e.target.value)}
        placeholder="e.g., IT Courses Comparison"
        className="comparison-name-input"
      />
      
      <div className="dialog-actions">
        <button className="btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button 
          className="btn-primary" 
          onClick={onSave}
          disabled={!comparisonName.trim()}
        >
          Save Comparison
        </button>
      </div>
    </div>
  </div>
);

export default CourseComparison;
